import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock3, History, PlayCircle, Tag, BookA } from "lucide-react";
import { useKnowledge } from "../../lib/knowledge.jsx";
import { AreaIcon, Eyebrow, Meter, StatusIcon } from "./shared";
import { SearchBox } from "./TopicTree";
import { cn } from "../../lib/utils";

function Hero() {
  const { index, progress } = useKnowledge();
  const tour = index?.start || [];
  const tourLeft = tour.find(s => !(progress?.completed || []).includes(s)) || tour[0];
  const started = tour.some(s => (progress?.topics || {})[s]);
  return (
    <section data-testid="kc-hero" className="relative overflow-hidden tile kc-rise">
      <img src="/knowledge/ai/welcome.webp" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30 dark:opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-r from-surface via-surface/90 to-surface/20" />
      <div className="relative px-6 sm:px-10 py-10 sm:py-14 max-w-2xl">
        <Eyebrow>1Rotary Control Tower · Knowledge Center</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight mt-3 leading-[1.05]">Learn the platform.<br />Decide with it.</h1>
        <p className="text-[14.5px] text-muted mt-4 leading-relaxed max-w-xl">
          Plain-language topics with worked examples, illustrations and a short quiz — from your first sign-in to ABC × XYZ, forecasting and the admin controls behind every number.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          {tourLeft && (
            <Link to={`/learn/${tourLeft}`} data-testid="kc-start-tour"
                  className="inline-flex items-center gap-2 h-10 px-4 bg-primary text-primary-fg rounded-sm text-[13px] font-semibold hover:opacity-90 active:scale-[0.98] transition-[opacity,transform]">
              <PlayCircle size={15} /> {started ? "Continue the Platform User Tour" : "Start here: Platform User Tour"}
            </Link>
          )}
          <SearchBox testid="kc-home-search" className="w-full sm:w-72 h-10 bg-surface/80" />
        </div>
      </div>
    </section>
  );
}

function Resume() {
  const { progress, cards } = useKnowledge();
  if (!progress) return null;
  const items = [
    progress.continue && { slug: progress.continue, label: "Continue where you left off", icon: History, testid: "kc-continue" },
    progress.next && progress.next !== progress.continue && { slug: progress.next, label: "Next suggested topic", icon: ArrowRight, testid: "kc-next-suggested" },
  ].filter(Boolean);
  return (
    <section className="grid grid-cols-1 md:grid-cols-[1.1fr_1fr] gap-4">
      <div className="tile p-5 kc-rise" style={{ animationDelay: "80ms" }} data-testid="kc-overall">
        <div className="flex items-baseline justify-between">
          <span className="kpi-label">Your progress</span>
          <span className="font-mono-tabular text-[12px] text-muted" data-testid="kc-overall-count">{progress.done} / {progress.total} topics</span>
        </div>
        <div className="font-mono-tabular text-4xl font-semibold mt-2 tracking-tight">{Math.round((progress.done / progress.total) * 100)}%</div>
        <Meter done={progress.done} total={progress.total} className="mt-3" testid="kc-overall-meter" />
        <p className="text-[11.5px] text-muted mt-2.5">A topic is complete when you score 4 of 5 or more on its quiz. Saved to your Employee ID.</p>
      </div>
      <div className="tile p-2 kc-rise" style={{ animationDelay: "140ms" }}>
        {items.length === 0 && <div className="p-4 text-[13px] text-success">Every topic complete — well done. Retake any quiz to refresh.</div>}
        {items.map(it => cards[it.slug] && (
          <Link key={it.testid} to={`/learn/${it.slug}`} data-testid={it.testid}
                className="flex items-center gap-3 p-3 rounded-sm hover:bg-surface-2 transition-colors group">
            <it.icon size={16} className="text-primary shrink-0" />
            <span className="flex-1 min-w-0">
              <span className="block text-[10px] uppercase tracking-widest text-muted">{it.label}</span>
              <span className="block text-[14px] font-medium truncate group-hover:text-primary transition-colors">{cards[it.slug].title}</span>
            </span>
            <ArrowRight size={14} className="text-muted group-hover:translate-x-0.5 transition-transform" />
          </Link>
        ))}
      </div>
    </section>
  );
}

function AreaCard({ area, i }) {
  const { progress, isDone } = useKnowledge();
  const done = progress?.areas.find(a => a.id === area.id)?.done ?? 0;
  const first = area.groups[0]?.topics[0];
  return (
    <div data-testid={`kc-area-${area.id}`} className="tile p-5 flex flex-col kc-rise hover:border-primary/40 transition-colors" style={{ animationDelay: `${200 + i * 60}ms` }}>
      <div className="flex items-start gap-3">
        <span className="w-9 h-9 rounded-sm bg-primary/10 text-primary flex items-center justify-center shrink-0"><AreaIcon icon={area.icon} size={17} /></span>
        <div className="min-w-0">
          <div className="text-[10px] font-mono-tabular text-subtle">AREA {i + 1}</div>
          <h2 className="font-display text-lg font-semibold tracking-tight leading-tight">{area.title}</h2>
        </div>
      </div>
      <p className="text-[12.5px] text-muted mt-2.5 leading-relaxed">{area.blurb}</p>
      <div className="flex items-center gap-2 mt-4">
        <Meter done={done} total={area.total} className="flex-1" testid={`kc-area-${area.id}-meter`} />
        <span data-testid={`kc-area-${area.id}-progress`} className="text-[11px] font-mono-tabular text-muted">{done}/{area.total}</span>
      </div>
      <ul className="mt-4 space-y-1 flex-1">
        {area.groups.flatMap(g => g.topics).map(t => (
          <li key={t.slug}>
            <Link to={`/learn/${t.slug}`} data-testid={`kc-area-topic-${t.slug}`} className="flex items-center gap-2 text-[12.5px] py-0.5 hover:text-primary transition-colors">
              <StatusIcon done={isDone(t.slug)} size={12} /><span className="truncate">{t.title}</span>
            </Link>
          </li>
        ))}
      </ul>
      {first && <Link to={`/learn/${first.slug}`} className="mt-4 text-[12px] text-primary font-medium inline-flex items-center gap-1 hover:gap-2 transition-[gap]" data-testid={`kc-area-${area.id}-open`}>Open area <ArrowRight size={12} /></Link>}
    </div>
  );
}

function Recent() {
  const { progress, cards } = useKnowledge();
  const recent = (progress?.recent || []).filter(r => cards[r.slug]).slice(0, 5);
  return (
    <div className="tile p-5" data-testid="kc-recent">
      <div className="kpi-label mb-3 flex items-center gap-1.5"><Clock3 size={11} /> Recently viewed</div>
      {recent.length === 0 && <p className="text-[12.5px] text-muted">Topics you open will appear here.</p>}
      <ul className="space-y-1.5">
        {recent.map(r => (
          <li key={r.slug}><Link to={`/learn/${r.slug}`} data-testid={`kc-recent-${r.slug}`} className="text-[13px] hover:text-primary transition-colors">{cards[r.slug].title}</Link></li>
        ))}
      </ul>
    </div>
  );
}

function Tags() {
  const { index } = useKnowledge();
  return (
    <div className="tile p-5" data-testid="kc-tags">
      <div className="kpi-label mb-3 flex items-center gap-1.5"><Tag size={11} /> Concept tags</div>
      <div className="flex flex-wrap gap-1.5">
        {(index?.tags || []).slice(0, 28).map(t => (
          <Link key={t.tag} to={`/learn/tag/${encodeURIComponent(t.tag)}`} data-testid={`kc-tag-${t.tag.replace(/\s+/g, "-")}`}
                className={cn("px-2 py-0.5 text-[11.5px] border border-border rounded-sm hover:border-primary/50 hover:text-primary transition-colors", t.count > 2 && "font-medium")}>
            {t.tag}<span className="text-subtle font-mono-tabular ml-1">{t.count}</span>
          </Link>
        ))}
      </div>
      <Link to="/learn/glossary" data-testid="kc-home-glossary" className="mt-4 inline-flex items-center gap-1.5 text-[12px] text-primary font-medium"><BookA size={12} /> Open the glossary</Link>
    </div>
  );
}

export default function KnowledgeHome() {
  const { index, error } = useKnowledge();
  if (error) return <div data-testid="kc-error" className="tile p-6 text-critical text-[13px]">{error}</div>;
  return (
    <div data-testid="kc-home" className="space-y-6">
      <Hero />
      <Resume />
      <section>
        <div className="flex items-end justify-between mb-3">
          <h2 className="font-display text-xl font-semibold tracking-tight">Learning areas</h2>
          <span className="text-[11px] text-muted font-mono-tabular">{index?.total ?? "…"} topics</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-4 gap-4">
          {index?.areas.map((a, i) => <AreaCard key={a.id} area={a} i={i} />)}
        </div>
      </section>
      <section className="grid grid-cols-1 lg:grid-cols-[1fr_1.6fr] gap-4"><Recent /><Tags /></section>
    </div>
  );
}
